// client/src/components/world/WorldScene.jsx
// Three.js сцена Мира. Итерация 4.1–4.4: локация + аватар + синхронизация.
// Итерация 8.3b: InteriorVisualizer (модули структуры) + fallback (космос/поверхность).
// Итерация 9.6: режимы просмотра (object / character) + SummaryMesh / SkyboxMesh.
// ⚠️ Внутри <Canvas> НЕЛЬЗЯ вызывать useGameSession / useSelector.
//    Всё — через props.

import React, { useMemo, useEffect } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, Environment } from '@react-three/drei';
import { Box, Alert, AlertTitle } from '@mui/material';

import SceneErrorBoundary from './SceneErrorBoundary';
import LocationVisualizer from './LocationVisualizer';
import CharacterAvatar from './CharacterAvatar';
import CosmicBackdrop from './CosmicBackdrop';
import StructureViewer from './StructureViewer';
import SkyboxMesh from './SkyboxMesh';

// ============================================================================
// WebGL-проверка (один раз)
// ============================================================================
function isWebGLAvailable() {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') ||
        canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
}

// ============================================================================
// Освещение
// ============================================================================
function Lighting() {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight
        position={[5, 10, 7]}
        intensity={1.2}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <pointLight position={[-5, 5, -5]} intensity={0.5} color="#00e5ff" />
    </>
  );
}

// ============================================================================
// Земля
// ============================================================================
function SceneGround() {
  return (
    <Grid
      position={[0, -0.01, 0]}
      args={[20, 20]}
      cellSize={1}
      cellThickness={0.5}
      cellColor="#2a2a3a"
      sectionSize={5}
      sectionThickness={1}
      sectionColor="#00e5ff"
      fadeDistance={30}
      fadeStrength={1.5}
      followCamera={false}
      infiniteGrid={false}
    />
  );
}

// ============================================================================
// Синхронизация камеры с персонажем
// ============================================================================
function CameraSync({ characterPosition }) {
  const controls = useThree((s) => s.controls);
  const invalidate = useThree((s) => s.invalidate);

  useEffect(() => {
    if (!controls || !characterPosition) return;
    controls.target.set(
      characterPosition.x ?? 0,
      characterPosition.y ?? 0,
      characterPosition.z ?? 0
    );
    controls.update();
    invalidate();
  }, [
    controls,
    invalidate,
    characterPosition?.x,
    characterPosition?.y,
    characterPosition?.z,
  ]);

  return null;
}

// ============================================================================
// Fallback-логика (когда нет модулей и нет summaryMesh)
// ============================================================================
const SYSTEM_TYPES = ['system', 'star_system', 'deep_space'];
const SURFACE_TYPES = ['planet', 'moon', 'asteroid', 'surface'];

function renderFallback({ locationInfo, characterMeta }) {
  const locationType = String(locationInfo?.type || '').toLowerCase();

  // 1. Космос (перелёт, глубокий космос, система)
  if (
    characterMeta?.in_transit ||
    SYSTEM_TYPES.includes(locationType)
  ) {
    return <CosmicBackdrop />;
  }

  // 2. Поверхность (планета, луна, астероид) — примитив
  if (
    characterMeta?.surface ||
    SURFACE_TYPES.includes(locationType)
  ) {
    return <LocationVisualizer locationInfo={locationInfo} />;
  }

  // 3. Структура без схемы / default — примитив
  return <LocationVisualizer locationInfo={locationInfo} />;
}

// ============================================================================
// Внутренняя сцена
// ============================================================================
function SceneContent({
  locationInfo,
  characterState,
  characterMeta,
  installedModules,
  currentDeckId,
  mode,
  summaryMesh,
  skybox,
}) {
  const characterPosition = characterState?.position || null;

  const hasModules =
    Array.isArray(installedModules) && installedModules.length > 0;
  const hasSummaryMesh = !!summaryMesh;
  const hasStructure = hasModules || hasSummaryMesh;

  // В режиме "object" — свободная камера; в "character" — слежение
  const isObjectMode = mode === 'object';

  return (
    <>
      <Lighting />
      <SceneGround />

      {/* 🔥 Скайбокс (звёзды, планеты, луны, distant_objects) */}
      {skybox && <SkyboxMesh skybox={skybox} />}

      {/* 🔥 Структура: режим object → SummaryMesh / все модули; character → ближайшие */}
      {hasStructure ? (
        <StructureViewer
          type={locationInfo?.type}
          id={locationInfo?.id}
          mode={isObjectMode ? 'object' : 'character'}
          summaryMesh={summaryMesh}
          installedModules={installedModules}
          currentDeckId={isObjectMode ? null : currentDeckId}
          characterPosition={isObjectMode ? null : characterPosition}
          visibilityRadius={isObjectMode ? null : 50}
          fallback={renderFallback({ locationInfo, characterMeta })}
        />
      ) : (
        renderFallback({ locationInfo, characterMeta })
      )}

      <CharacterAvatar characterState={characterState} />

      {/* OrbitControls — внутри Canvas, после сцены */}
      <OrbitControls
        enablePan
        enableZoom
        enableRotate
        minDistance={isObjectMode ? 2 : 1.5}
        maxDistance={isObjectMode ? 200 : 30}
        makeDefault
      />

      {/* CameraSync — только в character-режиме */}
      {!isObjectMode && <CameraSync characterPosition={characterPosition} />}

      <Environment preset="night" />
    </>
  );
}

// ============================================================================
// Публичный компонент
// ============================================================================
export default function WorldScene({
  locationInfo = null,
  characterState = null,
  characterMeta = null,
  installedModules = [],
  currentDeckId = null,
  mode = 'character',        // 🔥 "object" | "character"
  summaryMesh = null,        // 🔥 от useSummaryMesh
  skybox = null,             // 🔥 от useSkyboxMesh
  className = '',
  style = {},
}) {
  const webglOk = useMemo(() => isWebGLAvailable(), []);

  if (!webglOk) {
    return (
      <Box
        className={className}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <AlertTitle>3D недоступно</AlertTitle>
          Ваш браузер не поддерживает WebGL или аппаратное ускорение отключено.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 240,
        position: 'relative',
        bgcolor: '#0a0a12',
        borderRadius: 1,
        overflow: 'hidden',
        ...style,
      }}
    >
      <SceneErrorBoundary>
        <Canvas
          camera={{ position: [3, 3, 5], fov: 50, near: 0.1, far: 5000 }}
          dpr={[1, 2]}
          gl={{ antialias: true, alpha: false }}
          frameloop="always"
          shadows
          onCreated={({ gl }) => {
            gl.setClearColor('#0a0a12');
          }}
        >
          <SceneContent
            locationInfo={locationInfo}
            characterState={characterState}
            characterMeta={characterMeta}
            installedModules={installedModules}
            currentDeckId={currentDeckId}
            mode={mode}
            summaryMesh={summaryMesh}
            skybox={skybox}
          />
        </Canvas>
      </SceneErrorBoundary>
    </Box>
  );
}
